// ============================================================================
// Article Types
// ============================================================================

export interface ArticleAuthor {
  user_id: string;
  email: string;
  display_name?: string;
}

export interface Article {
  id: string;
  user_id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string | null;
  topic: string | null;
  tags: string[];
  time_to_read_minutes: number;
  is_public: boolean;
  view_count: number;
  comment_count: number;
  recording_count: number;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  author?: ArticleAuthor;
}

// ============================================================================
// Audio Recording Types
// ============================================================================

export interface AudioRecording {
  id: string;
  article_id: string;
  user_id: string;
  title: string;
  description: string | null;
  s3_key: string;
  file_size_bytes: number;
  duration_seconds: number | null;
  mime_type: string;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface NewAudioRecording {
  title: string;
  description?: string;
  s3_key: string;
  file_size_bytes: number;
  duration_seconds?: number;
  mime_type: string;
}

export interface UpdateAudioRecording {
  title?: string;
  description?: string | null;
  order_index?: number;
}

export interface ArticleShare {
  id: string;
  article_id: string;
  shared_with_user_id: string;
  shared_with_email: string;
  shared_with_display_name?: string;
  shared_by_user_id: string;
  created_at: string;
}

export interface ArticleComment {
  id: string;
  article_id: string;
  user_id: string;
  content: string;
  created_at: string;
  updated_at: string;
  author?: ArticleAuthor;
}

export interface ArticleTopic {
  id: number;
  name: string;
  display_order: number;
}

// ============================================================================
// Request Types
// ============================================================================

export interface NewArticle {
  title: string;
  content: string;
  excerpt?: string;
  topic?: string;
  tags?: string[];
  is_public?: boolean;
}

export interface UpdateArticle {
  title?: string;
  content?: string;
  excerpt?: string | null;
  topic?: string | null;
  tags?: string[];
  is_public?: boolean;
}

export interface ArticleFilters {
  search?: string;
  topic?: string;
  is_public?: boolean;
  limit?: number;
  offset?: number;
}

export interface PublicArticleFilters {
  search?: string;
  topic?: string;
  tags?: string[];
  limit?: number;
  offset?: number;
}

// ============================================================================
// Response Types
// ============================================================================

export interface CreateArticleResponse {
  articleId: string;
}

export interface GetArticleResponse {
  article: Article;
}

export interface ListArticlesResponse {
  articles: Article[];
  total: number;
}

export interface UpdateArticleResponse {
  article: Article;
}

export interface DeleteArticleResponse {
  success: boolean;
}

export interface ListTopicsResponse {
  topics: ArticleTopic[];
}

export interface ShareArticleResponse {
  share: ArticleShare;
}

export interface ListSharesResponse {
  shares: ArticleShare[];
}

export interface RecordViewResponse {
  isNewView: boolean;
  viewCount: number;
}

export interface ListCommentsResponse {
  comments: ArticleComment[];
}

export interface AddCommentResponse {
  comment: ArticleComment;
}

export interface UpdateCommentResponse {
  comment: ArticleComment;
}

export interface DeleteCommentResponse {
  success: boolean;
}

// ============================================================================
// Analytics Types
// ============================================================================

export interface ArticleAnalyticsResponse {
  totalArticles: number;
  publicArticles: number;
  totalViews: number;
  viewsTimeSeries: { date: string; views: number }[];
  topArticles: { articleId: string; title: string; viewCount: number; commentCount: number }[];
}

// ============================================================================
// Audio Recording Request/Response Types
// ============================================================================

export interface GetAudioUploadUrlRequest {
  filename: string;
  content_type: string;
  file_size: number;
}

export interface GetAudioUploadUrlResponse {
  uploadUrl: string;
  s3Key: string;
  expiresIn: number;
}

export interface CreateAudioRecordingResponse {
  recording: AudioRecording;
}

export interface ListAudioRecordingsResponse {
  recordings: AudioRecording[];
}

export interface GetAudioPlaybackUrlResponse {
  playbackUrl: string;
  expiresIn: number;
}

export interface UpdateAudioRecordingResponse {
  recording: AudioRecording;
}

export interface DeleteAudioRecordingResponse {
  success: boolean;
}

// ============================================================================
// Audio Recording Constants
// ============================================================================

export const AUDIO_CONSTRAINTS = {
  MAX_FILE_SIZE_BYTES: 50 * 1024 * 1024, // 50 MB
  MAX_RECORDINGS_PER_ARTICLE: 10,
  ALLOWED_MIME_TYPES: [
    'audio/webm',
    'audio/mp3',
    'audio/mpeg',
    'audio/wav',
    'audio/ogg',
    'audio/mp4',
    'audio/aac',
    'audio/x-m4a',
  ] as const,
  MAX_TITLE_LENGTH: 200,
  MAX_DESCRIPTION_LENGTH: 2000,
} as const;
